import { FaCalendarAlt, FaUsers, FaClock, FaCheck, FaTimes, FaCalendarPlus, FaGoogle, FaSpinner } from "react-icons/fa";
import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function MeetingScheduler({ members, onClose, onSchedule }) {
  const [step, setStep] = useState(1); // 1: Select attendees, 2: Date range, 3: Available slots, 4: Confirmation
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [requiredAttendees, setRequiredAttendees] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
    startTime: "09:00",
    endTime: "17:00",
  });
  const [duration, setDuration] = useState(60); // minutes
  const [meetingTitle, setMeetingTitle] = useState("Staff Council Meeting");
  const [meetingDescription, setMeetingDescription] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [authError, setAuthError] = useState(null);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [eventCreated, setEventCreated] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/google`);
      const data = await response.json();
      
      if (data.authUrl) {
        // Open Google OAuth in popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          data.authUrl,
          'Google Calendar Authorization',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Poll for popup closure
        const pollTimer = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollTimer);
            checkAuthStatus();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error initiating Google auth:', error);
      setAuthError('Failed to connect to Google Calendar');
    }
  };

  const createCalendarEvent = async () => {
    if (!selectedSlot) return;

    setIsCreatingEvent(true);
    setAuthError(null);

    try {
      // Get email addresses for selected attendees
      const attendeeEmails = selectedAttendees.map(id => {
        const member = members.find(m => m.id === id);
        return member?.email;
      }).filter(Boolean);

      // Create ISO datetime strings
      const startDateTime = `${selectedSlot.date}T${selectedSlot.startTime}`;
      const endDateTime = `${selectedSlot.date}T${selectedSlot.endTime}`;

      const response = await fetch(`${API_URL}/calendar/create-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          title: meetingTitle,
          description: meetingDescription || `Staff Council Meeting\n\nAttendees: ${selectedAttendees.length} members\nDuration: ${duration} minutes`,
          startDateTime,
          endDateTime,
          attendeeEmails,
          location: 'NYU Abu Dhabi'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create calendar event');
      }

      if (data.success) {
        setEventCreated(data.event);
        setStep(4); // Move to confirmation step
        
        // Also trigger the onSchedule callback if provided
        if (onSchedule) {
          onSchedule({
            title: meetingTitle,
            date: selectedSlot.date,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            attendees: selectedAttendees,
            calendarLink: data.event.htmlLink
          });
        }
      }
    } catch (error) {
      console.error('Error creating calendar event:', error);
      setAuthError(error.message || 'Failed to create calendar event. Please try again.');
    } finally {
      setIsCreatingEvent(false);
    }
  };

  const findAvailableSlots = async () => {
    if (!dateRange.startDate) return;

    if (!isAuthenticated) {
      setAuthError('Please connect to Google Calendar first');
      return;
    }

    setIsLoadingSlots(true);
    setAuthError(null);

    try {
      // Generate potential time slots
      const slots = [];
      const start = new Date(dateRange.startDate);
      // If no end date, use start date (search within single day)
      const end = new Date(dateRange.endDate || dateRange.startDate);
      const startHour = parseInt(dateRange.startTime.split(":")[0]);
      const startMin = parseInt(dateRange.startTime.split(":")[1]);
      const endHour = parseInt(dateRange.endTime.split(":")[0]);

      // Generate time slots for each day in range
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        // Skip weekends
        if (d.getDay() === 0 || d.getDay() === 6) continue;

        for (let hour = startHour; hour < endHour; hour++) {
          for (let minute = 0; minute < 60; minute += 30) {
            const slotStartTime = new Date(d);
            slotStartTime.setHours(hour, minute, 0);
            
            const slotEndTime = new Date(slotStartTime);
            slotEndTime.setMinutes(slotEndTime.getMinutes() + duration);

            // Check if slot is within working hours
            if (slotEndTime.getHours() > endHour || 
                (slotEndTime.getHours() === endHour && slotEndTime.getMinutes() > 0)) continue;

            slots.push({
              date: new Date(d).toISOString().split("T")[0],
              startTime: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00`,
              endTime: `${slotEndTime.getHours().toString().padStart(2, "0")}:${slotEndTime.getMinutes().toString().padStart(2, "0")}:00`,
            });
          }
        }
      }

      // Get email addresses for selected attendees
      const emails = selectedAttendees.map(id => {
        const member = members.find(m => m.id === id);
        return member?.email;
      }).filter(Boolean);

      // Check availability via API
      const response = await fetch(`${API_URL}/calendar/check-slots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          emails,
          slots: slots.slice(0, 50) // Check first 50 slots to avoid overwhelming the API
        })
      });

      if (!response.ok) {
        throw new Error('Failed to check calendar availability');
      }

      const data = await response.json();

      if (data.success) {
        // Filter slots where all required attendees are available
        const validSlots = data.slots.filter(slot => {
          // Check if all required attendees are available
          const requiredEmails = requiredAttendees.map(id => {
            const member = members.find(m => m.id === id);
            return member?.email;
          }).filter(Boolean);

          const allRequiredAvailable = requiredEmails.every(
            email => !slot.unavailableEmails.includes(email)
          );

          return allRequiredAvailable;
        });

        // Map unavailable emails to names
        const slotsWithNames = validSlots.map(slot => ({
          ...slot,
          unavailableMembers: slot.unavailableEmails.map(email => {
            const member = members.find(m => m.email === email);
            return member?.name || email;
          })
        }));

        setAvailableSlots(slotsWithNames.slice(0, 20)); // Show top 20 slots
        setStep(3);
      } else {
        throw new Error(data.error || 'Failed to check availability');
      }

    } catch (error) {
      console.error('Error finding available slots:', error);
      setAuthError(error.message || 'Failed to check calendar availability. Please try again.');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const toggleAttendee = (memberId) => {
    setSelectedAttendees((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const toggleRequired = (memberId) => {
    if (!selectedAttendees.includes(memberId)) return;
    setRequiredAttendees((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  // Legacy function removed - now using createCalendarEvent with Google Calendar API
  const addToCalendarLegacy = () => {
    if (!selectedSlot) return;

    // This is now handled by createCalendarEvent() which uses Google Calendar API
    // Keeping this as reference for fallback .ics file download if needed
    const startDateTime = new Date(`${selectedSlot.date}T${selectedSlot.startTime}`);
    const endDateTime = new Date(`${selectedSlot.date}T${selectedSlot.endTime}`);

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Staff Council//Meeting Scheduler//EN
BEGIN:VEVENT
UID:${Date.now()}@staffcouncil.com
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(startDateTime)}
DTEND:${formatDate(endDateTime)}
SUMMARY:${meetingTitle || "Staff Council Meeting"}
DESCRIPTION:${meetingDescription || ""}
LOCATION:NYU Abu Dhabi
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `meeting-${selectedSlot.date}.ics`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const sendInvites = () => {
    // This is now handled automatically by Google Calendar API
    // when createCalendarEvent() is called with sendUpdates: 'all'
    const attendeeList = selectedAttendees
      .map((id) => members.find((m) => m.id === id)?.name)
      .join(", ");

    console.log(
      `Meeting invites sent via Google Calendar to:\n${attendeeList}\n\nMeeting: ${meetingTitle}\nDate: ${
        selectedSlot?.date
      }\nTime: ${selectedSlot?.startTime} - ${selectedSlot?.endTime}`
    );

    if (onSchedule) {
      onSchedule({
        title: meetingTitle,
        description: meetingDescription,
        date: selectedSlot?.date,
        time: selectedSlot?.startTime,
        attendees: selectedAttendees,
        requiredAttendees: requiredAttendees,
      });
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          maxWidth: "900px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#2d3748" }}>
              Smart Meeting Scheduler
            </h2>
            <p style={{ margin: "4px 0 0 0", color: "#718096", fontSize: "14px" }}>
              Step {step} of 4
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              color: "#718096",
              cursor: "pointer",
              padding: "8px",
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{ padding: "0 24px" }}>
          <div
            style={{
              height: "4px",
              background: "#e2e8f0",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${(step / 4) * 100}%`,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                transition: "width 0.3s",
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "24px" }}>
          {/* Google Calendar Authentication Banner */}
          {!isCheckingAuth && !isAuthenticated && (
            <div
              style={{
                padding: "16px",
                background: "#fef5e7",
                border: "1px solid #f39c12",
                borderRadius: "8px",
                marginBottom: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: "600", color: "#d97706", marginBottom: "4px" }}>
                  Google Calendar Connection Required
                </div>
                <div style={{ fontSize: "14px", color: "#4a5568" }}>
                  Connect your Google Calendar to check real availability
                </div>
              </div>
              <button
                onClick={handleGoogleAuth}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  background: "#4285f4",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                <FaGoogle />
                Connect Calendar
              </button>
            </div>
          )}

          {isAuthenticated && (
            <div
              style={{
                padding: "12px 16px",
                background: "#e6f7ee",
                border: "1px solid #38a169",
                borderRadius: "8px",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                color: "#38a169",
              }}
            >
              <FaCheck />
              <span>Google Calendar connected - real availability will be checked</span>
            </div>
          )}

          {authError && (
            <div
              style={{
                padding: "12px 16px",
                background: "#fee",
                border: "1px solid #c53030",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "14px",
                color: "#c53030",
              }}
            >
              {authError}
            </div>
          )}

          {/* Step 1: Select Attendees */}
          {step === 1 && (
            <div>
              <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
                Select Attendees
              </h3>
              <p style={{ color: "#718096", marginBottom: "20px", fontSize: "14px" }}>
                Choose who should attend and mark required attendees whose availability is mandatory.
              </p>

              <div style={{ marginBottom: "20px" }}>
                <input
                  type="text"
                  placeholder="Meeting Title"
                  value={meetingTitle}
                  onChange={(e) => setMeetingTitle(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "15px",
                    marginBottom: "12px",
                  }}
                />
                <textarea
                  placeholder="Meeting Description (optional)"
                  value={meetingDescription}
                  onChange={(e) => setMeetingDescription(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "15px",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: "12px",
                  maxHeight: "400px",
                  overflow: "auto",
                }}
              >
                {members.map((member) => (
                  <div
                    key={member.id}
                    style={{
                      padding: "12px",
                      border: selectedAttendees.includes(member.id)
                        ? "2px solid #667eea"
                        : "1px solid #e2e8f0",
                      borderRadius: "8px",
                      background: selectedAttendees.includes(member.id) ? "#f7faff" : "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onClick={() => toggleAttendee(member.id)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                      <div>
                        <div style={{ fontWeight: "600", color: "#2d3748", fontSize: "14px" }}>
                          {member.name}
                        </div>
                        <div style={{ fontSize: "12px", color: "#718096" }}>{member.role}</div>
                      </div>
                      {selectedAttendees.includes(member.id) && (
                        <FaCheck style={{ color: "#667eea" }} />
                      )}
                    </div>
                    {selectedAttendees.includes(member.id) && (
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "8px",
                          fontSize: "13px",
                          cursor: "pointer",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={requiredAttendees.includes(member.id)}
                          onChange={() => toggleRequired(member.id)}
                        />
                        <span style={{ color: "#4a5568" }}>Required Attendee</span>
                      </label>
                    )}
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: "24px",
                  padding: "12px",
                  background: "#f7fafc",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#4a5568",
                }}
              >
                <strong>{selectedAttendees.length}</strong> attendees selected •{" "}
                <strong>{requiredAttendees.length}</strong> required
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={selectedAttendees.length === 0 || !meetingTitle}
                style={{
                  marginTop: "20px",
                  width: "100%",
                  padding: "12px",
                  background:
                    selectedAttendees.length === 0 || !meetingTitle
                      ? "#cbd5e0"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: selectedAttendees.length === 0 || !meetingTitle ? "not-allowed" : "pointer",
                }}
              >
                Next: Set Date Range
              </button>
            </div>
          )}

          {/* Step 2: Date Range */}
          {step === 2 && (
            <div>
              <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
                Set Date and Time Range
              </h3>
              <p style={{ color: "#718096", marginBottom: "20px", fontSize: "14px" }}>
                Choose the date range and time preferences for finding available meeting slots.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600" }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600" }}>
                    End Date <span style={{ color: "#94a3b8", fontWeight: "400", fontSize: "12px" }}>(optional - defaults to start date)</span>
                  </label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600" }}>
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={dateRange.startTime}
                    onChange={(e) => setDateRange({ ...dateRange, startTime: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600" }}>
                    End Time
                  </label>
                  <input
                    type="time"
                    value={dateRange.endTime}
                    onChange={(e) => setDateRange({ ...dateRange, endTime: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600" }}>
                  Meeting Duration (minutes)
                </label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => setStep(1)}
                  disabled={isLoadingSlots}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#e2e8f0",
                    color: "#4a5568",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: isLoadingSlots ? "not-allowed" : "pointer",
                  }}
                >
                  Back
                </button>
                <button
                  onClick={findAvailableSlots}
                  disabled={!dateRange.startDate || isLoadingSlots}
                  style={{
                    flex: 2,
                    padding: "12px",
                    background:
                      !dateRange.startDate || isLoadingSlots
                        ? "#cbd5e0"
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: !dateRange.startDate || isLoadingSlots ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {isLoadingSlots ? (
                    <>
                      <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
                      Checking Calendars...
                    </>
                  ) : (
                    "Find Available Times"
                  )}
                </button>
              </div>
              <style>{`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* Step 3: Available Slots */}
          {step === 3 && (
            <div>
              <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
                Available Time Slots
              </h3>
              <p style={{ color: "#718096", marginBottom: "20px", fontSize: "14px" }}>
                Times are sorted by availability. Green = optimal, Yellow = good, showing top matches.
              </p>

              <div style={{ maxHeight: "450px", overflow: "auto" }}>
                {availableSlots.length > 0 ? (
                  availableSlots.map((slot, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedSlot(slot)}
                      style={{
                        padding: "16px",
                        border:
                          selectedSlot === slot ? "2px solid #667eea" : "1px solid #e2e8f0",
                        borderRadius: "8px",
                        marginBottom: "12px",
                        cursor: "pointer",
                        background: selectedSlot === slot ? "#f7faff" : "white",
                        transition: "all 0.2s",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: "15px", fontWeight: "600", color: "#2d3748", marginBottom: "4px" }}>
                            {new Date(slot.date).toLocaleDateString("en-US", {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            })}
                          </div>
                          <div style={{ fontSize: "14px", color: "#4a5568" }}>
                            {slot.startTime} - {slot.endTime}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              background:
                                slot.availabilityPercentage === 100
                                  ? "#e6f7ee"
                                  : slot.availabilityPercentage >= 80
                                  ? "#fef5e7"
                                  : "#fee",
                              color:
                                slot.availabilityPercentage === 100
                                  ? "#38a169"
                                  : slot.availabilityPercentage >= 80
                                  ? "#d97706"
                                  : "#c53030",
                            }}
                          >
                            {slot.availabilityPercentage}% available
                          </div>
                          <div style={{ fontSize: "12px", color: "#718096", marginTop: "4px" }}>
                            {slot.availableCount}/{slot.totalCount} attendees
                          </div>
                        </div>
                      </div>
                      {slot.unavailableMembers.length > 0 && (
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#718096",
                            paddingTop: "8px",
                            borderTop: "1px solid #f7fafc",
                          }}
                        >
                          Unavailable: {slot.unavailableMembers.join(", ")}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#718096" }}>
                    No available slots found. Try adjusting your date range or attendees.
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
                <button
                  onClick={() => setStep(2)}
                  disabled={isCreatingEvent}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#e2e8f0",
                    color: "#4a5568",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: isCreatingEvent ? "not-allowed" : "pointer",
                  }}
                >
                  Back
                </button>
                <button
                  onClick={createCalendarEvent}
                  disabled={!selectedSlot || isCreatingEvent}
                  style={{
                    flex: 2,
                    padding: "12px",
                    background: !selectedSlot || isCreatingEvent
                      ? "#cbd5e0"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: !selectedSlot || isCreatingEvent ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {isCreatingEvent ? (
                    <>
                      <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
                      Creating Event...
                    </>
                  ) : (
                    <>
                      <FaCalendarPlus />
                      Add to Staff Council Calendar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Success Confirmation */}
          {step === 4 && selectedSlot && (
            <div>
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    background: "#e6f7ee",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <FaCheck style={{ fontSize: "28px", color: "#38a169" }} />
                </div>
                <h3 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "8px", color: "#38a169" }}>
                  Meeting Successfully Scheduled!
                </h3>
                <p style={{ color: "#718096", fontSize: "14px" }}>
                  The event has been added to the Staff Council calendar and invitations have been sent to all attendees
                </p>
              </div>

              <div
                style={{
                  background: "#f7fafc",
                  padding: "20px",
                  borderRadius: "12px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", color: "#718096", marginBottom: "4px" }}>
                    MEETING TITLE
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "600", color: "#2d3748" }}>
                    {meetingTitle}
                  </div>
                </div>

                {meetingDescription && (
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontSize: "12px", color: "#718096", marginBottom: "4px" }}>
                      DESCRIPTION
                    </div>
                    <div style={{ fontSize: "14px", color: "#4a5568" }}>
                      {meetingDescription}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", color: "#718096", marginBottom: "4px" }}>
                    DATE & TIME
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "600", color: "#2d3748" }}>
                    {new Date(selectedSlot.date).toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <div style={{ fontSize: "14px", color: "#4a5568", marginTop: "4px" }}>
                    {selectedSlot.startTime} - {selectedSlot.endTime} (UAE Time)
                  </div>
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", color: "#718096", marginBottom: "8px" }}>
                    ATTENDEES ({selectedAttendees.length}) - Invites Sent ✓
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {selectedAttendees.map((id) => {
                      const member = members.find((m) => m.id === id);
                      const isRequired = requiredAttendees.includes(id);
                      return (
                        <span
                          key={id}
                          style={{
                            padding: "4px 10px",
                            background: isRequired ? "#e6f7ee" : "white",
                            border: isRequired ? "1px solid #38a169" : "1px solid #e2e8f0",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#4a5568",
                          }}
                        >
                          {member?.name}
                          {isRequired && " *"}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {eventCreated && eventCreated.htmlLink && (
                  <div 
                    style={{ 
                      marginTop: "16px",
                      padding: "12px",
                      background: "#e6f7ee",
                      borderRadius: "8px",
                      border: "1px solid #38a169"
                    }}
                  >
                    <div style={{ fontSize: "12px", color: "#38a169", marginBottom: "8px", fontWeight: "600" }}>
                      📅 CALENDAR EVENT CREATED
                    </div>
                    <a 
                      href={eventCreated.htmlLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: "#667eea",
                        fontSize: "14px",
                        textDecoration: "underline",
                        wordBreak: "break-all"
                      }}
                    >
                      View in Google Calendar →
                    </a>
                  </div>
                )}
              </div>

              <div
                style={{
                  background: "#fef5e7",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  fontSize: "14px",
                  color: "#4a5568",
                  border: "1px solid #f39c12"
                }}
              >
                <strong>📧 What happens next:</strong>
                <ul style={{ marginTop: "8px", marginLeft: "20px" }}>
                  <li>All attendees will receive a Google Calendar invitation via email</li>
                  <li>The meeting is now visible on the Staff Council shared calendar</li>
                  <li>Attendees can accept/decline the invitation</li>
                  <li>You'll receive notifications about attendance responses</li>
                </ul>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                {eventCreated && eventCreated.htmlLink && (
                  <button
                    onClick={() => window.open(eventCreated.htmlLink, '_blank')}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "white",
                      color: "#667eea",
                      border: "2px solid #667eea",
                      borderRadius: "8px",
                      fontSize: "15px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <FaCalendarAlt />
                    View in Calendar
                  </button>
                )}
                <button
                  onClick={onClose}
                  style={{
                    flex: eventCreated ? 1 : 2,
                    padding: "12px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  {eventCreated ? "Done" : "Close"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

